import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL, MEDIA_PRESIGN_URL } from '../../config/api';
import { toast } from 'react-toastify';
import { getCurrentUser } from '../../utils/auth';

const AttachMediaModal = ({ isOpen, onClose, orderNo, roomTasks }) => {
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaCaption, setMediaCaption] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [selectedTaskForMedia, setSelectedTaskForMedia] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total }
  const mediaFileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && orderNo) {
      fetchAttachments(orderNo);
    }
  }, [isOpen, orderNo]);

  // Keyboard nav for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setLightboxIndex(i => (i + 1) % attachments.length);
      if (e.key === 'ArrowLeft')  setLightboxIndex(i => (i - 1 + attachments.length) % attachments.length);
      if (e.key === 'Escape')     setLightboxIndex(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, attachments.length]);

  // Escape to close modal (when lightbox is not open)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && lightboxIndex === null) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, lightboxIndex, onClose]);

  const fetchAttachments = async (no) => {
    setAttachmentsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/media/${no}`);
      setAttachments(response.data.attachments || []);
    } catch (err) {
      console.error('Failed to load attachments:', err);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  // Uploads a single file. Throws on failure; caller decides how to report it.
  const uploadSingleFile = async (file) => {
    let mimeType = file.type;
    if (!mimeType) {
      const ext = file.name.split('.').pop().toLowerCase();
      const extMap = {
        jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
        gif: 'image/gif', webp: 'image/webp', heic: 'image/heic',
        heif: 'image/heif', mp4: 'video/mp4', mov: 'video/quicktime',
        avi: 'video/x-msvideo', mkv: 'video/x-matroska',
      };
      mimeType = extMap[ext] || '';
    }

    const isVideo = mimeType.startsWith('video/');
    const isImage = mimeType.startsWith('image/');
    if (!isVideo && !isImage) {
      throw new Error(`${file.name}: only image and video files are supported.`);
    }

    const uploadMimeType = (mimeType === 'image/heic' || mimeType === 'image/heif')
      ? 'image/jpeg'
      : mimeType;

    const presignRes = await axios.post(`${MEDIA_PRESIGN_URL}/api/media/presign`, {
      orderNo,
      fileName:    file.name,
      contentType: uploadMimeType,
    });
    const { uploadUrl, publicUrl } = presignRes.data;

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', uploadMimeType);
      xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`R2 upload failed: ${xhr.status}`));
      xhr.onerror = () => reject(new Error('R2 upload network error'));
      xhr.send(file);
    });

    await axios.post(`${API_URL}/api/media/save`, {
      orderNo,
      taskId:     selectedTaskForMedia?.id || null,
      roomNo:     selectedTaskForMedia?.roomNo || null,
      mediaUrl:   publicUrl,
      mediaType:  isVideo ? 'video' : 'image',
      fileName:   file.name,
      caption:    mediaCaption,
      uploadedBy: getCurrentUser(),
    });
  };

  // Uploads one or more files sequentially, reporting a single combined result.
  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0 || !orderNo) return;

    setUploadingMedia(true);
    let successCount = 0;
    const errors = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress({ current: i + 1, total: files.length });
        try {
          await uploadSingleFile(files[i]);
          successCount++;
        } catch (err) {
          console.error('Upload failed:', err);
          const msg = err?.response?.data?.message || err?.message || String(err);
          errors.push(msg);
        }
      }

      setMediaCaption('');
      if (successCount > 0) await fetchAttachments(orderNo);

      if (successCount > 0 && errors.length === 0) {
        toast.success(files.length > 1 ? `${successCount} files uploaded successfully.` : 'Media uploaded successfully.');
      } else if (successCount > 0 && errors.length > 0) {
        toast.warn(`${successCount} uploaded, ${errors.length} failed: ${errors[0]}`);
      } else if (errors.length > 0) {
        toast.error(`Upload failed: ${errors[0]}`);
      }
    } finally {
      setUploadingMedia(false);
      setUploadProgress(null);
    }
  };

  // Paste image(s) from clipboard (Ctrl+V)
  useEffect(() => {
    if (!isOpen) return;
    const onPaste = async (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter(item => item.type.startsWith('image/'));
      if (imageItems.length === 0) return;
      const files = imageItems.map((imageItem, idx) => {
        const blob = imageItem.getAsFile();
        if (!blob) return null;
        const ext = imageItem.type === 'image/jpeg' ? 'jpg' : (imageItem.type.split('/')[1] || 'png');
        return new File([blob], `pasted-image-${Date.now()}-${idx}.${ext}`, { type: imageItem.type });
      }).filter(Boolean);
      if (files.length > 0) await uploadFiles(files);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [isOpen, uploadFiles]);

  const handleMediaUpload = async (e) => {
    const files = e.target.files;
    const fileList = files ? Array.from(files) : [];
    e.target.value = '';
    if (fileList.length > 0) await uploadFiles(fileList);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) await uploadFiles(files);
  };

  const copyMediaLink = async (mediaUrl) => {
    try {
      await navigator.clipboard.writeText(mediaUrl);
      toast.success('Link copied.');
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('Failed to copy link.');
    }
  };

  const downloadMedia = async (mediaUrl, fileName) => {
    try {
      const res = await fetch(mediaUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed, opening in new tab instead:', err);
      window.open(mediaUrl, '_blank', 'noopener');
    }
  };

  const handleDeleteAttachment = async (id, mediaUrl) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      await axios.delete(`${API_URL}/api/media/${id}`, {
        headers: { 'X-R2-Url': mediaUrl || '' },
      });
      setAttachments(prev => prev.filter(a => a.ID !== id));
      toast.success('Attachment removed.');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to remove attachment.');
    }
  };

  if (!isOpen) return null;

  const photoCount = attachments.filter(a => a.MediaType === 'image').length;
  const videoCount = attachments.filter(a => a.MediaType === 'video').length;
  const lightboxItem = lightboxIndex !== null ? attachments[lightboxIndex] : null;
  const openSlide = (idx) => setLightboxIndex(idx);
  const closeSlide = () => setLightboxIndex(null);
  const prevSlide = (e) => { e.stopPropagation(); setLightboxIndex(i => (i - 1 + attachments.length) % attachments.length); };
  const nextSlide = (e) => { e.stopPropagation(); setLightboxIndex(i => (i + 1) % attachments.length); };

  return (
    <>
      {/* Modal backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {/* Modal panel */}
        <div
          onClick={e => e.stopPropagation()}
          onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
          onDragEnter={e => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false); }}
          onDrop={handleDrop}
          style={{
            background: 'var(--dark-surface, #fff)',
            borderRadius: '16px',
            width: 'min(720px, 95vw)',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
            border: isDragOver ? '2px dashed #2d6a9f' : '2px solid transparent',
            transition: 'border-color 0.2s',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Drag overlay */}
          {isDragOver && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(45,106,159,0.15)', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', borderRadius: '14px' }}>
              <i className="fas fa-cloud-upload-alt" style={{ fontSize: '48px', color: '#2d6a9f', marginBottom: '12px' }}></i>
              <span style={{ fontSize: '18px', fontWeight: '600', color: '#2d6a9f' }}>Drop to upload</span>
            </div>
          )}

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-photo-video" style={{ color: 'var(--text-primary)', fontSize: '16px' }}></i>
              <span style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '20px' }}>Media</span>
              {attachments.length > 0 && (
                <div style={{ display: 'flex', gap: '6px' }}>
                  {photoCount > 0 && (
                    <span style={{ background: 'rgba(45,106,159,0.15)', color: '#2d6a9f', borderRadius: '12px', padding: '2px 10px', fontSize: '12px' }}>
                      <i className="fas fa-image mr-1"></i>{photoCount}
                    </span>
                  )}
                  {videoCount > 0 && (
                    <span style={{ background: 'rgba(45,106,159,0.15)', color: '#2d6a9f', borderRadius: '12px', padding: '2px 10px', fontSize: '12px' }}>
                      <i className="fas fa-film mr-1"></i>{videoCount}
                    </span>
                  )}
                </div>
              )}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '26px', cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1, padding: '0 4px' }}>&times;</button>
          </div>

          {/* Upload controls */}
          <div className="media-upload-row" style={{ flexShrink: 0 }}>
            <div className="media-task-selector">
              <span className="media-task-label">Attach to:</span>
              <select
                className="search-input"
                style={{ color: 'var(--text-secondary)', fontWeight: 700, fontStyle: 'italic', letterSpacing: '0.3px', textIndent: 'var(--spacing-md)' }}
                value={selectedTaskForMedia?.id || ''}
                onChange={e => {
                  const task = (roomTasks || []).find(t => String(t.id) === e.target.value);
                  setSelectedTaskForMedia(task || null);
                }}
              >
                <option value=''>Order (no specific task)</option>
                {(roomTasks || []).map(t => (
                  <option key={t.id} value={t.id}>
                    Room {t.roomNo} — {t.roomTask}{t.assignedWorker && t.assignedWorker !== 'Select Worker' ? ` (${t.assignedWorker})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="media-caption-bar">
              <input
                type="text"
                className="search-input"
                value={mediaCaption}
                onChange={e => setMediaCaption(e.target.value)}
                placeholder="Caption (optional)"
              />
              <input ref={mediaFileInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={handleMediaUpload} disabled={uploadingMedia} />
              <button
                className="btn btn-success rounded-pill shadow-sm"
                onClick={() => mediaFileInputRef.current?.click()}
                disabled={uploadingMedia}
              >
                {uploadingMedia
                  ? <><i className="fas fa-spinner fa-spin"></i> {uploadProgress && uploadProgress.total > 1 ? `Uploading ${uploadProgress.current}/${uploadProgress.total}…` : 'Uploading…'}</>
                  : <><i className="fas fa-paperclip"></i> Attach Media</>
                }
              </button>
            </div>
          </div>

          {/* Drag hint */}
          <div style={{ textAlign: 'center', fontSize: '22px', color: 'var(--text-secondary, #adb5bd)', padding: '6px', opacity: 0.7, flexShrink: 0 }}>
           drag &amp; drop / paste <kbd style={{ fontSize: '18px', padding: '1px 4px', borderRadius: '3px', border: '1px solid currentColor', opacity: 0.7 }}>Ctrl+V</kbd> files anywhere in this window
          </div>

          {/* Gallery (scrollable) */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 20px' }}>
            {attachmentsLoading && (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
              </div>
            )}

            {!attachmentsLoading && attachments.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 0', color: '#adb5bd' }}>
                <i className="fas fa-photo-video" style={{ fontSize: '42px', marginBottom: '12px', display: 'block' }}></i>
                <span style={{ fontSize: '14px' }}>No media attached yet</span>
                <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.7 }}>Upload a file above or drag &amp; drop it here</div>
              </div>
            )}

            {!attachmentsLoading && attachments.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                {attachments.map((att, idx) => (
                  <div
                    key={att.ID}
                    style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', background: att.MediaType === 'video' ? '#1a1a2e' : '#f8f9fa', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.18)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
                  >
                    {/* Thumbnail */}
                    <div onClick={() => openSlide(idx)} style={{ position: 'relative', height: '120px' }}>
                      {att.MediaType === 'image' ? (
                        <img src={att.MediaURL} alt={att.Caption || att.FileName} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          <i className="fas fa-play-circle" style={{ fontSize: '36px', color: 'rgba(255,255,255,0.9)' }}></i>
                          <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '1px' }}>Video</span>
                        </div>
                      )}
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.25)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}
                      >
                        <i className="fas fa-expand" style={{ color: '#fff', fontSize: '18px', opacity: 0, transition: 'opacity 0.2s' }}
                          onMouseEnter={e => e.currentTarget.style.opacity = 1}
                        ></i>
                      </div>
                      <span style={{ position: 'absolute', top: '6px', left: '8px', background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: '10px', borderRadius: '4px', padding: '1px 6px' }}>
                        {idx + 1}/{attachments.length}
                      </span>
                    </div>

                    {/* Footer */}
                    <div style={{ padding: '7px 9px', background: '#fff' }}>
                      {att.TaskID && (() => {
                        const t = (roomTasks || []).find(t => t.id === att.TaskID);
                        return t ? (
                          <div style={{ fontSize: '10px', color: '#fff', background: '#2d6a9f', borderRadius: '4px', padding: '1px 6px', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            Room {t.roomNo} · {t.roomTask}
                          </div>
                        ) : null;
                      })()}
                      <div style={{ fontSize: '11px', color: '#495057', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '4px' }}>
                        {att.Caption || att.FileName}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '10px', color: '#adb5bd' }}>{new Date(att.UploadedDate).toLocaleDateString()}</span>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            onClick={e => { e.stopPropagation(); copyMediaLink(att.MediaURL); }}
                            style={{ background: 'none', border: 'none', color: '#495057', cursor: 'pointer', padding: '0', fontSize: '11px', lineHeight: 1 }}
                            title="Copy public link"
                          >
                            <i className="fas fa-link"></i>
                          </button>
                          <button
                            onClick={e => { e.stopPropagation(); downloadMedia(att.MediaURL, att.FileName); }}
                            style={{ background: 'none', border: 'none', color: '#495057', cursor: 'pointer', padding: '0', fontSize: '11px', lineHeight: 1 }}
                            title="Download"
                          >
                            <i className="fas fa-download"></i>
                          </button>
                          <button
                            onClick={e => { e.stopPropagation(); handleDeleteAttachment(att.ID, att.MediaURL); }}
                            style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: '0', fontSize: '11px', lineHeight: 1 }}
                            title="Remove"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox (rendered above the modal backdrop) */}
      {lightboxItem && (
        <div
          onClick={closeSlide}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.94)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          {/* Top bar */}
          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)' }}>
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
              {lightboxItem.Caption || lightboxItem.FileName}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>{lightboxIndex + 1} / {attachments.length}</span>
              <button onClick={() => copyMediaLink(lightboxItem.MediaURL)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '16px', cursor: 'pointer', lineHeight: 1, padding: 0 }} title="Copy public link">
                <i className="fas fa-link"></i>
              </button>
              <button onClick={() => downloadMedia(lightboxItem.MediaURL, lightboxItem.FileName)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '16px', cursor: 'pointer', lineHeight: 1, padding: 0 }} title="Download">
                <i className="fas fa-download"></i>
              </button>
              <button onClick={closeSlide} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '26px', cursor: 'pointer', lineHeight: 1, padding: 0 }}>&times;</button>
            </div>
          </div>

          {/* Prev arrow */}
          {attachments.length > 1 && (
            <button
              onClick={prevSlide}
              style={{ position: 'absolute', left: '16px', background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: '50%', width: '48px', height: '48px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s', zIndex: 1 }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
            >‹</button>
          )}

          {/* Media */}
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {lightboxItem.MediaType === 'image' ? (
              <img src={lightboxItem.MediaURL} alt={lightboxItem.Caption || lightboxItem.FileName} style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: '6px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', objectFit: 'contain' }} />
            ) : (
              <video key={lightboxItem.MediaURL} src={lightboxItem.MediaURL} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: '6px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} />
            )}
          </div>

          {/* Next arrow */}
          {attachments.length > 1 && (
            <button
              onClick={nextSlide}
              style={{ position: 'absolute', right: '16px', background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: '50%', width: '48px', height: '48px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s', zIndex: 1 }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
            >›</button>
          )}

          {/* Dot indicators */}
          {attachments.length > 1 && (
            <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: '20px', display: 'flex', gap: '8px' }}>
              {attachments.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  style={{ width: i === lightboxIndex ? '24px' : '8px', height: '8px', borderRadius: '4px', border: 'none', background: i === lightboxIndex ? '#fff' : 'rgba(255,255,255,0.35)', padding: 0, cursor: 'pointer', transition: 'all 0.25s' }}
                />
              ))}
            </div>
          )}

          {/* Bottom meta */}
          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: '44px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            {lightboxItem.TaskID && (() => {
              const t = (roomTasks || []).find(t => t.id === lightboxItem.TaskID);
              return t ? (
                <span style={{ background: 'rgba(45,106,159,0.8)', color: '#fff', borderRadius: '12px', padding: '2px 12px', fontSize: '12px' }}>
                  Room {t.roomNo} · {t.roomTask}
                </span>
              ) : null;
            })()}
            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>
              {lightboxItem.UploadedBy && <>{lightboxItem.UploadedBy} · </>}{new Date(lightboxItem.UploadedDate).toLocaleDateString()}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default AttachMediaModal;
